import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { COMPANY_CONFIG, formatCurrency } from "@/config/company.config";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { 
  Briefcase, Search, Plus, Clock, CheckCircle, AlertTriangle, 
  Calendar, DollarSign, User, FileText, ChevronRight 
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Mandate {
  id: string;
  clientId: string;
  clientName: string;
  dealId?: string;
  dealName?: string;
  type: 'legal' | 'tax' | 'financial' | 'immigration' | 'due_diligence' | 'other';
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'review' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: any;
  dueDate: any;
  completedDate?: any;
  hourlyRate: number;
  hoursWorked: number;
  estimatedHours: number;
  totalFee: number;
  currency: string;
  milestones: Milestone[];
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  dueDate: any;
  completed: boolean;
  completedDate?: any;
}

const mockMandates: Mandate[] = [
  {
    id: "mock-mandate-1",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    dealId: "deal-123",
    dealName: "Acquisition Tech Solutions",
    type: "legal",
    title: "Due Diligence Juridique - Tech Solutions",
    description: "Analyse complète des contrats et obligations légales pour l'acquisition",
    status: "in_progress",
    priority: "high",
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
    hourlyRate: 250,
    hoursWorked: 45,
    estimatedHours: 80,
    totalFee: 20000,
    currency: COMPANY_CONFIG.currency,
    milestones: [
      { id: "m1", title: "Revue initiale", description: "Revue des documents", dueDate: new Date(), completed: true },
      { id: "m2", title: "Analyse contrats", description: "Analyse approfondie", dueDate: new Date(), completed: true },
      { id: "m3", title: "Rapport final", description: "Livraison rapport", dueDate: new Date(), completed: false },
    ],
  },
  {
    id: "mock-mandate-2",
    clientId: "mock-client-2",
    clientName: "Sophie Tremblay",
    type: "tax",
    title: "Planification Fiscale - Vente d'Entreprise",
    description: "Optimisation fiscale pour la vente de l'entreprise familiale",
    status: "in_progress",
    priority: "medium",
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
    hourlyRate: 300,
    hoursWorked: 20,
    estimatedHours: 50,
    totalFee: 15000,
    currency: COMPANY_CONFIG.currency,
    milestones: [
      { id: "m1", title: "Analyse fiscale", description: "Évaluation situation", dueDate: new Date(), completed: true },
      { id: "m2", title: "Stratégie", description: "Plan d'optimisation", dueDate: new Date(), completed: false },
    ],
  },
  {
    id: "mock-mandate-3",
    clientId: "mock-client-3",
    clientName: "Marc Dubois",
    type: "financial",
    title: "Évaluation Financière - Portfolio",
    description: "Analyse financière du portfolio de 5 entreprises",
    status: "completed",
    priority: "high",
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000),
    dueDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    completedDate: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
    hourlyRate: 275,
    hoursWorked: 65,
    estimatedHours: 60,
    totalFee: 17875,
    currency: COMPANY_CONFIG.currency,
    milestones: [
      { id: "m1", title: "Collecte données", description: "Rassembler documents", dueDate: new Date(), completed: true },
      { id: "m2", title: "Analyse", description: "Évaluation complète", dueDate: new Date(), completed: true },
      { id: "m3", title: "Livraison", description: "Rapport final", dueDate: new Date(), completed: true },
    ],
  },
  {
    id: "mock-mandate-4",
    clientId: "mock-client-1",
    clientName: "Jean-Pierre Martin",
    type: "immigration",
    title: "Visa Investisseur - Processus Complet",
    description: "Accompagnement pour obtention visa investisseur",
    status: "pending",
    priority: "urgent",
    startDate: new Date(),
    dueDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000),
    hourlyRate: 350,
    hoursWorked: 5,
    estimatedHours: 40,
    totalFee: 14000,
    currency: COMPANY_CONFIG.currency,
    milestones: [],
  },
];

const translations = {
  en: {
    title: "Mandates",
    subtitle: "Manage your active and completed mandates",
    newMandate: "New Mandate",
    searchMandates: "Search mandates...",
    allStatus: "All Status",
    pending: "Pending",
    inProgress: "In Progress",
    review: "Review",
    completed: "Completed",
    cancelled: "Cancelled",
    noMandates: "No mandates yet",
    createFirstMandate: "Create Your First Mandate",
    dueDate: "Due Date",
    startDate: "Start Date",
    client: "Client",
    progress: "Progress",
    hours: "Hours",
    rate: "Rate",
    totalFee: "Total Fee",
    milestones: "Milestones",
    viewDetails: "View Details",
    updateProgress: "Update Progress",
    loading: "Loading mandates...",
    legal: "Legal Services",
    tax: "Tax Advisory",
    financial: "Financial Analysis",
    immigration: "Immigration Law",
    due_diligence: "Due Diligence",
    other: "Other Services",
    low: "Low",
    medium: "Medium",
    high: "High",
    urgent: "Urgent",
    daysLeft: "days left",
    overdue: "Overdue",
    completedOn: "Completed on",
  },
  fr: {
    title: "Mandats",
    subtitle: "Gérez vos mandats actifs et complétés",
    newMandate: "Nouveau Mandat",
    searchMandates: "Rechercher mandats...",
    allStatus: "Tous Statuts",
    pending: "En Attente",
    inProgress: "En Cours",
    review: "En Révision",
    completed: "Complété",
    cancelled: "Annulé",
    noMandates: "Aucun mandat pour le moment",
    createFirstMandate: "Créez Votre Premier Mandat",
    dueDate: "Date Limite",
    startDate: "Date Début",
    client: "Client",
    progress: "Progression",
    hours: "Heures",
    rate: "Taux",
    totalFee: "Frais Totaux",
    milestones: "Jalons",
    viewDetails: "Voir Détails",
    updateProgress: "Mettre à Jour",
    loading: "Chargement des mandats...",
    legal: "Services Juridiques",
    tax: "Conseil Fiscal",
    financial: "Analyse Financière",
    immigration: "Droit Immigration",
    due_diligence: "Due Diligence",
    other: "Autres Services",
    low: "Basse",
    medium: "Moyenne",
    high: "Haute",
    urgent: "Urgent",
    daysLeft: "jours restants",
    overdue: "En retard",
    completedOn: "Complété le",
  },
};

export default function ConsultantMandates() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [mandates, setMandates] = useState<Mandate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadMandates();
    }
  }, [user]);

  const loadMandates = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const mandatesRef = collection(db, "consultantMandates");
      const mandatesQuery = query(
        mandatesRef,
        where("consultantId", "==", user.email),
        orderBy("startDate", "desc")
      );
      const mandatesSnapshot = await getDocs(mandatesQuery);
      const mandatesData = mandatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Mandate[];
      setMandates(mandatesData.length > 0 ? mandatesData : mockMandates);
    } catch (error) {
      console.error("Error loading mandates:", error);
      setMandates(mockMandates);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMandates = mandates.filter(mandate => {
    const matchesSearch = mandate.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mandate.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mandate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || mandate.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getTypeLabel = (type: string) => {
    return t[type as keyof typeof t] || type;
  };

  const getPriorityLabel = (priority: string) => {
    return t[priority as keyof typeof t] || priority;
  };

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (status) {
      case 'in_progress': return 'default';
      case 'completed': return 'secondary';
      case 'pending': return 'outline';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getPriorityBadgeVariant = (priority: string): "default" | "secondary" | "outline" | "destructive" => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      default: return 'outline';
    }
  };

  const formatMandateCurrency = (amount: number, currency: string = COMPANY_CONFIG.currency) => {
    return formatCurrency(amount, currency);
  };

  const getProgress = (mandate: Mandate) => {
    if (mandate.estimatedHours === 0) return 0;
    return Math.min(100, Math.round((mandate.hoursWorked / mandate.estimatedHours) * 100));
  };

  const getDaysUntilDue = (dueDate: any) => {
    const due = dueDate instanceof Date ? dueDate : dueDate?.toDate?.() || new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (date: any) => {
    const d = date instanceof Date ? date : date?.toDate?.() || new Date(date);
    return format(d, 'dd MMM yyyy', { locale: language === 'fr' ? fr : undefined });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Button onClick={() => navigate('/dashboard/consultant/new-mandate')}>
          <Plus className="mr-2 h-4 w-4" />
          {t.newMandate}
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t.searchMandates}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full md:w-[180px]">
            <SelectValue placeholder={t.allStatus} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allStatus}</SelectItem>
            <SelectItem value="pending">{t.pending}</SelectItem>
            <SelectItem value="in_progress">{t.inProgress}</SelectItem>
            <SelectItem value="review">{t.review}</SelectItem>
            <SelectItem value="completed">{t.completed}</SelectItem>
            <SelectItem value="cancelled">{t.cancelled}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Mandates List */}
      {filteredMandates.length > 0 ? (
        <div className="space-y-4">
          {filteredMandates.map((mandate) => {
            const daysLeft = getDaysUntilDue(mandate.dueDate);
            const progress = getProgress(mandate);
            const completedMilestones = mandate.milestones.filter(m => m.completed).length;
            
            return (
              <Card key={mandate.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant={getStatusBadgeVariant(mandate.status)}>
                          {t[mandate.status as keyof typeof t] || mandate.status}
                        </Badge>
                        <Badge variant={getPriorityBadgeVariant(mandate.priority)}>
                          {getPriorityLabel(mandate.priority)}
                        </Badge>
                        <Badge variant="outline">{getTypeLabel(mandate.type)}</Badge>
                      </div>
                      <CardTitle className="text-lg">{mandate.title}</CardTitle>
                      <CardDescription className="mt-1">{mandate.description}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {mandate.status !== 'completed' && (
                        <div className={`text-sm font-medium flex items-center gap-1 ${
                          daysLeft < 0 ? 'text-destructive' : daysLeft <= 7 ? 'text-amber-600' : 'text-muted-foreground'
                        }`}>
                          {daysLeft < 0 ? (
                            <>
                              <AlertTriangle className="h-4 w-4" />
                              {t.overdue}
                            </>
                          ) : (
                            <>
                              <Clock className="h-4 w-4" />
                              {daysLeft} {t.daysLeft}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t.client}</p>
                        <p className="text-sm font-medium">{mandate.clientName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t.dueDate}</p>
                        <p className="text-sm font-medium">{formatDate(mandate.dueDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t.hours}</p>
                        <p className="text-sm font-medium">{mandate.hoursWorked} / {mandate.estimatedHours}h</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t.totalFee}</p>
                        <p className="text-sm font-medium">{formatMandateCurrency(mandate.totalFee, mandate.currency)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t.progress}</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {mandate.milestones.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{t.milestones}:</span>
                      <span className="font-medium">{completedMilestones} / {mandate.milestones.length}</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <FileText className="h-4 w-4 mr-1" />
                      {t.viewDetails}
                    </Button>
                    {mandate.status !== 'completed' && (
                      <Button size="sm" className="flex-1">
                        {t.updateProgress}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">{t.noMandates}</h3>
            <p className="text-muted-foreground text-center mt-2 mb-4">
              {language === 'fr' 
                ? 'Créez votre premier mandat pour commencer à travailler avec vos clients'
                : 'Create your first mandate to start working with your clients'}
            </p>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t.createFirstMandate}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
